import type { Dispatch, MutableRefObject } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import type { CockpitArtifactView } from '../model/cockpitViewModel';
import type { CockpitDataSource } from './CockpitDataSource';
import { asCockpitError, isCockpitForbidden } from './cockpitControllerErrors';
import { mergeArtifactPages } from './cockpitControllerMerge';
import type {
  CockpitControllerAction,
  CockpitControllerState,
} from './cockpitControllerTypes';
import { CockpitRequestCoordinator } from './cockpitRequestCoordinator';

export function useCockpitArtifactContent({
  activityId,
  dataSource,
  artifacts,
  stateRef,
  dispatch,
}: {
  activityId: string;
  dataSource: CockpitDataSource;
  artifacts: readonly CockpitArtifactView[];
  stateRef: MutableRefObject<CockpitControllerState>;
  dispatch: Dispatch<CockpitControllerAction>;
}) {
  const requests = useRef(new Map<string, CockpitRequestCoordinator>()).current;
  useArtifactRequestCleanup(activityId, requests);
  useAuthorizationCleanup(artifacts, requests);
  const loadPage = useArtifactPageLoader({
    activityId,
    dataSource,
    stateRef,
    dispatch,
    requests,
  });
  return useArtifactContentActions(stateRef, loadPage);
}

function useAuthorizationCleanup(
  artifacts: readonly CockpitArtifactView[],
  requests: Map<string, CockpitRequestCoordinator>,
): void {
  useEffect(() => {
    const inspectable = new Set(
      artifacts
        .filter(artifact => artifact.access === 'inspectable')
        .map(artifact => artifact.id),
    );
    requests.forEach((request, artifactId) => {
      if (inspectable.has(artifactId)) return;
      request.cancel();
      requests.delete(artifactId);
    });
  }, [artifacts, requests]);
}

function useArtifactRequestCleanup(
  activityId: string,
  requests: Map<string, CockpitRequestCoordinator>,
): void {
  useEffect(
    () => () => {
      requests.forEach(request => request.cancel());
      requests.clear();
    },
    [activityId, requests],
  );
}

function useArtifactPageLoader({
  activityId,
  dataSource,
  stateRef,
  dispatch,
  requests,
}: ArtifactRequestContext) {
  return useCallback(
    (artifactId: string, offset = 0, accumulated: ContentPage | null = null) =>
      loadArtifactPage(
        { activityId, dataSource, stateRef, dispatch, requests },
        artifactId,
        offset,
        accumulated,
      ),
    [activityId, dataSource, dispatch, requests, stateRef],
  );
}

function useArtifactContentActions(
  stateRef: MutableRefObject<CockpitControllerState>,
  loadPage: (
    artifactId: string,
    offset?: number,
    accumulated?: ContentPage | null,
  ) => Promise<ContentPage>,
) {
  const loadArtifactContent = useCallback(
    async (artifactId: string) => {
      const cached = stateRef.current.artifactContent[artifactId]?.page;
      let page = cached ?? (await loadPage(artifactId));
      while (page.contentPage.hasMore) {
        page = await loadPage(artifactId, page.content.length, page);
      }
      return page.content;
    },
    [loadPage, stateRef],
  );

  const loadMoreArtifactContent = useCallback(
    async (artifactId: string) => {
      const current = stateRef.current.artifactContent[artifactId]?.page;
      if (!current?.contentPage.hasMore) return current?.content ?? '';
      return (await loadPage(artifactId, current.content.length, current))
        .content;
    },
    [loadPage, stateRef],
  );

  return { loadArtifactContent, loadMoreArtifactContent };
}

async function loadArtifactPage(
  context: ArtifactRequestContext,
  artifactId: string,
  offset: number,
  accumulated: ContentPage | null,
) {
  const coordinator = coordinatorFor(context.requests, artifactId);
  const request = coordinator.begin();
  context.dispatch({ type: 'artifactStarted', artifactId });
  try {
    const page = await context.dataSource.getArtifactContent({
      activityId: context.activityId,
      artifactId,
      offset,
      signal: request.signal,
    });
    validateArtifactPage(page, context.activityId, artifactId);
    const current =
      accumulated ??
      context.stateRef.current.artifactContent[artifactId]?.page ??
      null;
    const merged = mergeArtifactPages(current, page);
    if (page.contentPage.hasMore && merged.content.length <= offset) {
      throw new Error('Artifact pagination did not advance.');
    }
    if (!coordinator.succeed(request, Date.now())) throw supersededRequest();
    context.dispatch({ type: 'artifactSucceeded', artifactId, page });
    return merged;
  } catch (reason) {
    if (!coordinator.isCurrent(request)) throw reason;
    coordinator.finish(request);
    const error = asCockpitError(reason);
    context.dispatch({
      type: 'artifactFailed',
      artifactId,
      error,
      forbidden: isCockpitForbidden(error),
    });
    throw error;
  }
}

function validateArtifactPage(
  page: ContentPage,
  activityId: string,
  artifactId: string,
): void {
  if (page.activityId !== activityId || page.artifactId !== artifactId) {
    throw new Error('Artifact content does not match the request.');
  }
}

function supersededRequest(): DOMException {
  return new DOMException('Artifact request was superseded.', 'AbortError');
}

type ContentPage = Awaited<ReturnType<CockpitDataSource['getArtifactContent']>>;

interface ArtifactRequestContext {
  activityId: string;
  dataSource: CockpitDataSource;
  stateRef: MutableRefObject<CockpitControllerState>;
  dispatch: Dispatch<CockpitControllerAction>;
  requests: Map<string, CockpitRequestCoordinator>;
}

function coordinatorFor(
  requests: Map<string, CockpitRequestCoordinator>,
  artifactId: string,
): CockpitRequestCoordinator {
  const existing = requests.get(artifactId);
  if (existing) return existing;
  const created = new CockpitRequestCoordinator();
  requests.set(artifactId, created);
  return created;
}
